import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquareWarning, RefreshCw, CheckCircle, Clock, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getProblemReports, updateProblemReport } from '../../config/api';

const CATEGORY_LABEL = { BUG: '🐞 Bug', FEATURE: '💡 Feature', DATA_ISSUE: '📊 Data', QUESTION: '❓ Question', OTHER: '✍️ Other' };
const STATUS_PILL = {
  OPEN: 'bg-amber-100 text-amber-800',
  IN_REVIEW: 'bg-blue-100 text-blue-800',
  RESOLVED: 'bg-green-100 text-green-800'
};

const fmt = (d) => (d ? new Date(d).toLocaleString() : '');

const ProblemReports = () => {
  const qc = useQueryClient();
  const [status, setStatus] = useState('ALL');
  const [category, setCategory] = useState('ALL');
  const [selected, setSelected] = useState(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['problem-reports', status, category],
    queryFn: () => getProblemReports({ status, category, limit: 100 })
  });

  const mutation = useMutation({
    mutationFn: ({ id, payload }) => updateProblemReport(id, payload),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['problem-reports'] });
      setSelected(null);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update')
  });

  const reports = data?.reports || [];

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <MessageSquareWarning className="h-6 w-6 text-rose-600" />
            Problem Reports
          </h1>
          <p className="mt-1 text-gray-600">
            User-submitted issues and requests from the in-app “Report Problem” button.
            {data?.openCount != null && <span className="ml-2 font-medium text-amber-700">{data.openCount} open</span>}
          </p>
        </div>
        <button onClick={() => refetch()} title="Refresh" className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
          <option value="ALL">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="IN_REVIEW">In Review</option>
          <option value="RESOLVED">Resolved</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
          <option value="ALL">All categories</option>
          {Object.keys(CATEGORY_LABEL).map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['When', 'Category', 'Screen', 'Reported by', 'Notes', 'Status', ''].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No reports match these filters.</td></tr>
            ) : reports.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{fmt(r.createdAt)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-sm">{CATEGORY_LABEL[r.category] || r.category}</td>
                <td className="px-4 py-3 text-sm">
                  <div className="text-gray-900">{r.screenTitle || '—'}</div>
                  <div className="font-mono text-xs text-gray-400">{r.screen}</div>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{r.userEmail || '—'}</td>
                <td className="max-w-xs px-4 py-3 text-sm text-gray-700"><div className="line-clamp-2">{r.notes}</div></td>
                <td className="whitespace-nowrap px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status.replace('_', ' ')}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <button onClick={() => setSelected(r)} className="text-blue-600 hover:text-blue-800" title="View"><Eye className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{CATEGORY_LABEL[selected.category]} · {selected.screenTitle}</h3>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL[selected.status]}`}>{selected.status.replace('_', ' ')}</span>
            </div>
            <div className="space-y-4 px-5 py-4 text-sm">
              <div><div className="text-xs uppercase text-gray-400">Notes</div><p className="mt-1 whitespace-pre-wrap text-gray-800">{selected.notes}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-xs uppercase text-gray-400">Reported by</div><div className="text-gray-800">{selected.userEmail || '—'}</div></div>
                <div><div className="text-xs uppercase text-gray-400">When</div><div className="text-gray-800">{fmt(selected.createdAt)}</div></div>
                <div className="col-span-2"><div className="text-xs uppercase text-gray-400">Page URL</div><div className="break-all font-mono text-xs text-gray-600">{selected.pageUrl}</div></div>
              </div>
              {selected.context && (
                <div>
                  <div className="text-xs uppercase text-gray-400">Context</div>
                  <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-600">{JSON.stringify(selected.context, null, 2)}</pre>
                </div>
              )}
              {selected.userAgent && (
                <div><div className="text-xs uppercase text-gray-400">Browser</div><div className="break-all text-xs text-gray-500">{selected.userAgent}</div></div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button onClick={() => mutation.mutate({ id: selected.id, payload: { status: 'IN_REVIEW' } })} title="Mark In Review" disabled={mutation.isPending}
                className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50">
                <Clock className="h-4 w-4" /> Mark In Review
              </button>
              <button onClick={() => mutation.mutate({ id: selected.id, payload: { status: 'RESOLVED' } })} title="Mark Resolved" disabled={mutation.isPending}
                className="inline-flex items-center gap-1 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100 disabled:opacity-50">
                <CheckCircle className="h-4 w-4" /> Mark Resolved
              </button>
              <button onClick={() => setSelected(null)} title="Close" className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemReports;
